import React from 'react';
import { useBizEntityCatalog } from '@saviynt/biz-entity-catalog';
import { Button, Icon, Menu, type MenuOption } from '@saviynt/design-system';

import { useInvertedLocalize } from '../../../shared/utils/localization';
import { type Client } from '../types';

import styles from './ClientCredentialsListPage.module.css';

/** Kebab menu action keys */
const ACTION_ENABLE_DISABLE = 'toggle-status';
const ACTION_DELETE = 'delete';

interface ActionsCellProps {
  item: Client;
  onDelete: (client: Client) => void;
}

/** Row action cell with Edit button and kebab menu (Enable/Disable, Delete) */
export function ActionsCell({ item, onDelete }: ActionsCellProps): JSX.Element {
  const l = useInvertedLocalize();
  const { openEditModal, openDisableModal, enableItem } =
    useBizEntityCatalog<Client>();

  const kebabOptions: MenuOption[] = [
    {
      label: item.enabled ? l('Disable') : l('Enable'),
      value: ACTION_ENABLE_DISABLE,
    },
    {
      label: l('Delete'),
      value: ACTION_DELETE,
      color: 'critical' as const,
    },
  ];

  const handleKebabChange = (option: MenuOption) => {
    if (option.value === ACTION_ENABLE_DISABLE) {
      if (item.enabled) {
        // Disable goes through BEC's confirmation prompt
        openDisableModal(item);
      } else {
        // Enable fires directly (no confirmation needed)
        enableItem(item.id as string | number).catch(() => {});
      }
    } else if (option.value === ACTION_DELETE) {
      onDelete(item);
    }
  };

  return (
    <div className={styles.ActionsCell}>
      <Button
        kind='subtle'
        size='small'
        onClick={() => openEditModal(item)}
        ariaLabel={l('Edit {name}', { name: item.clientName })}>
        {l('Edit')}
      </Button>
      <Menu
        options={kebabOptions}
        onChange={handleKebabChange}
        size='small'
        trigger={
          <Button
            kind='ghost'
            size='small'
            leftIcon={<Icon kind='MoreVertical' size='small' />}
            ariaLabel={l('More actions for {name}', {
              name: item.clientName,
            })}
          />
        }
      />
    </div>
  );
}
